import type { Playlist } from '../page';

interface PlaylistSwitcherProps {
  playlists: Playlist[];
  selectedPlaylistId: number;
  onSelectPlaylist: (id: number) => void;
  onGoToManage?: () => void;
}

/**
 * Lets users pick which playlist is active on the home “Playlist” tab.
 * Selection state is shared with Manage — switching here updates the same library.
 */
export default function PlaylistSwitcher({
  playlists,
  selectedPlaylistId,
  onSelectPlaylist,
  onGoToManage,
}: PlaylistSwitcherProps) {
  const formatCreatedDate = (value?: string) => {
    if (!value) return 'Date unknown';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'Date unknown';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatOwnerHandle = (value?: string | null) => {
    const raw = (value || '').trim();
    if (!raw) return 'unknown';
    if (raw.includes('@')) return raw.split('@')[0] || 'unknown';
    return raw;
  };

  const visibilityLabel = (value?: 'public' | 'followers' | 'private') => {
    if (value === 'followers') return 'Followers only';
    if (value === 'private') return 'Private';
    return 'Public';
  };

  if (playlists.length === 0) {
    return (
      <div
        id="playlist-switcher"
        className="mb-6 rounded-[1.5rem] border border-dashed border-stone-300 bg-stone-50/90 px-5 py-6 text-center shadow-sm"
      >
        <p className="text-sm font-medium text-stone-800">No playlists yet</p>
        <p className="mt-1 text-sm text-stone-600">
          Create a playlist in Manage, then come back here to play it.
        </p>
        {onGoToManage && (
          <button
            type="button"
            onClick={onGoToManage}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#6B3B18] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95"
          >
            <i className="ri-folder-music-line" aria-hidden />
            Open Manage
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      id="playlist-switcher"
      className="mb-6 rounded-[1.5rem] border border-stone-200/80 bg-white/90 p-4 shadow-sm md:p-5"
    >
      <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">Your playlists</p>
          <p className="mt-1 text-sm text-stone-600">
            Choose which list to use, then tap a track below to play.
          </p>
        </div>
        {onGoToManage && (
          <button
            type="button"
            onClick={onGoToManage}
            className="shrink-0 text-sm font-medium text-amber-900/80 underline-offset-2 hover:underline"
          >
            Edit in Manage
          </button>
        )}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-stone-300">
        {playlists.map((p) => {
          const active = selectedPlaylistId === p.id;
          const handle = formatOwnerHandle(p.ownerUsername);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelectPlaylist(p.id)}
              className={`min-w-[10rem] max-w-[15rem] shrink-0 snap-start rounded-2xl border px-4 py-3 text-left transition ${
                active
                  ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200/70'
                  : 'border-stone-200 bg-stone-50/80 hover:border-amber-200 hover:bg-white'
              }`}
            >
              {/* Author avatar */}
              <div className="mb-2 flex items-center gap-2">
                {p.ownerAvatarUrl ? (
                  <img
                    src={p.ownerAvatarUrl}
                    alt={handle}
                    className="h-9 w-9 rounded-full object-cover ring-2 ring-stone-200 shrink-0"
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-sm font-bold text-amber-800 uppercase ring-2 ring-stone-200">
                    {handle[0] ?? '?'}
                  </span>
                )}
                <span className="min-w-0 truncate text-xs text-stone-500">@{handle}</span>
              </div>

              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 truncate font-semibold text-stone-900">{p.name}</p>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                    p.visibility === 'private'
                      ? 'bg-red-100 text-red-700'
                      : p.visibility === 'followers'
                        ? 'bg-sky-100 text-sky-700'
                        : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  {visibilityLabel(p.visibility)}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-stone-500">
                {p.tracks.length} track{p.tracks.length === 1 ? '' : 's'}
              </p>
              <p className="mt-0.5 text-[11px] text-stone-400">
                {formatCreatedDate(p.createdAt)}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
